import { z } from 'zod';
import { TimestampSchema } from '../common.js';
import { BrandColorSchema } from '../entities/tenant.js';
import { AgentSchema, CtaDestinationSchema, CtaSchema, DEFAULT_AGENT_BEHAVIOR_POLICY, Iso639_1Schema, } from '../entities/agent.js';
import { AgentMediaSchema, AgentMediaSlotSchema } from '../entities/agentMedia.js';
import { OpenAIVoiceSchema } from '../entities/agentVoiceProfile.js';
// =========================================================
// List / detail response shapes
// =========================================================
/** Safe subset of AgentSchema for list responses. */
export const AgentSummarySchema = AgentSchema.pick({
    agentId: true,
    tenantId: true,
    slug: true,
    name: true,
    status: true,
    voiceProvider: true,
    interactionMode: true,
    textChatFallbackEnabled: true,
    createdAt: true,
    updatedAt: true,
});
/**
 * Per-media projection returned inside `AgentDetail.media`. Extends the
 * storage row with a `publicUrl` resolved from the agent-runtime
 * CloudFront domain (`AGENT_MEDIA_CDN_BASE` env var on the router
 * Lambda), so the dashboard can render previews without having to
 * reconstruct the URL from `s3Key` and the CDN base itself.
 */
export const AgentMediaDetailSchema = AgentMediaSchema.extend({
    /** Same-origin (agent-runtime) URL for GET; null when CDN base is unset. */
    publicUrl: z.string().url().nullable(),
});
/** Full agent detail with associated instruction metadata and media assets. */
export const AgentDetailSchema = AgentSchema.extend({
    /** Instruction metadata per language (body omitted — fetch individually). */
    instructions: z.array(z.object({
        lang: Iso639_1Schema,
        updatedAt: TimestampSchema,
    })),
    media: z.array(AgentMediaDetailSchema),
});
// =========================================================
// Create / update request shapes
// =========================================================
export const AgentCreateRequestSchema = z
    .object({
    name: z.string().min(1).max(120),
    slug: AgentSchema.shape.slug,
    responseLength: AgentSchema.shape.responseLength.optional(),
    interactionMode: AgentSchema.shape.interactionMode,
    textChatFallbackEnabled: AgentSchema.shape.textChatFallbackEnabled,
    lipAnimationEnabled: AgentSchema.shape.lipAnimationEnabled,
    voiceProvider: AgentSchema.shape.voiceProvider,
    voiceProviderConfig: AgentSchema.shape.voiceProviderConfig,
    languages: AgentSchema.shape.languages,
    activeLanguages: AgentSchema.shape.activeLanguages.optional(),
    defaultLang: AgentSchema.shape.defaultLang,
    localizedContent: AgentSchema.shape.localizedContent.optional(),
    backgroundKind: AgentSchema.shape.backgroundKind.default('color'),
    backgroundValue: AgentSchema.shape.backgroundValue.default('#000000'),
    ctas: AgentSchema.shape.ctas.default([]),
    videoMediaId: AgentSchema.shape.videoMediaId.optional(),
    hevcVideoMediaId: AgentSchema.shape.hevcVideoMediaId,
    posterMediaId: AgentSchema.shape.posterMediaId.optional(),
})
    .refine((v) => v.languages.includes(v.defaultLang), {
    message: 'defaultLang must be one of the values in languages.',
    path: ['defaultLang'],
})
    .refine((v) => (v.activeLanguages ?? []).every((lang) => v.languages.includes(lang)), {
    message: 'activeLanguages must be a subset of languages.',
    path: ['activeLanguages'],
})
    // `voiceProvider` and `voiceProviderConfig.voiceProvider` are a discriminated
    // pair — they must never drift. Previously only the per-provider runtime
    // (`openai.ts`) threw on mismatch, which meant bad rows could be persisted
    // and only surface at voice-session mint time.
    .refine((v) => v.voiceProvider === v.voiceProviderConfig.voiceProvider, {
    message: 'voiceProvider must match voiceProviderConfig.voiceProvider.',
    path: ['voiceProviderConfig', 'voiceProvider'],
});
export const AgentUpdateRequestSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    slug: AgentSchema.shape.slug.optional(),
    responseLength: AgentSchema.shape.responseLength.optional(),
    status: AgentSchema.shape.status.optional(),
    interactionMode: AgentSchema.shape.interactionMode.optional(),
    textChatFallbackEnabled: AgentSchema.shape.textChatFallbackEnabled.optional(),
    lipAnimationEnabled: AgentSchema.shape.lipAnimationEnabled.optional(),
    voiceProvider: AgentSchema.shape.voiceProvider.optional(),
    voiceProviderConfig: AgentSchema.shape.voiceProviderConfig.optional(),
    languages: AgentSchema.shape.languages.optional(),
    activeLanguages: AgentSchema.shape.activeLanguages.optional(),
    defaultLang: AgentSchema.shape.defaultLang.optional(),
    localizedContent: AgentSchema.shape.localizedContent.optional(),
    backgroundKind: AgentSchema.shape.backgroundKind.optional(),
    backgroundValue: AgentSchema.shape.backgroundValue.optional(),
    ctas: AgentSchema.shape.ctas.optional(),
    videoMediaId: AgentSchema.shape.videoMediaId.optional(),
    hevcVideoMediaId: AgentSchema.shape.hevcVideoMediaId,
    posterMediaId: AgentSchema.shape.posterMediaId.optional(),
})
    .refine((v) => {
    if (v.defaultLang !== undefined && v.languages !== undefined) {
        return v.languages.includes(v.defaultLang);
    }
    return true;
}, { message: 'defaultLang must be one of the values in languages.', path: ['defaultLang'] })
    .refine((v) => {
    if (v.activeLanguages !== undefined && v.languages !== undefined) {
        return v.activeLanguages.every((lang) => v.languages.includes(lang));
    }
    return true;
}, {
    message: 'activeLanguages must be a subset of languages.',
    path: ['activeLanguages'],
})
    // Same pair-check as create. On update: if only one of the two is present,
    // the caller is asking to switch provider without repointing the config —
    // reject. If both are present, they must agree.
    .refine((v) => {
    if (v.voiceProvider === undefined && v.voiceProviderConfig === undefined)
        return true;
    if (v.voiceProvider === undefined || v.voiceProviderConfig === undefined)
        return false;
    return v.voiceProvider === v.voiceProviderConfig.voiceProvider;
}, {
    message: 'voiceProvider and voiceProviderConfig must be updated together and must match; update both fields in the same PATCH.',
    path: ['voiceProviderConfig', 'voiceProvider'],
});
export const AgentGuardrailPromptUpdateRequestSchema = z.object({
    guardrailPrompt: AgentSchema.shape.guardrailPrompt.unwrap(),
    behaviorPolicy: AgentSchema.shape.behaviorPolicy.optional(),
});
// =========================================================
// Media upload request / response
// =========================================================
export const AgentMediaUploadRequestSchema = z.object({
    slot: AgentMediaSlotSchema,
    lang: Iso639_1Schema.optional(),
    mime: z.string().min(1),
    bytes: z
        .number()
        .int()
        .positive()
        .max(100 * 1024 * 1024),
});
export const AgentMediaUploadResponseSchema = z.object({
    mediaId: z.string().uuid(),
    uploadUrl: z.string().url(),
    publicUrl: z.string().url(),
    expiresAt: TimestampSchema,
});
// =========================================================
// Public agent config (embed runtime — no credentials, no instructions)
// =========================================================
export const AgentPublicLocalizedConfigSchema = z.object({
    lang: Iso639_1Schema,
    name: z.string().min(1),
    videoUrl: z.string().url().nullable(),
    /** Safari-compatible HEVC-with-alpha companion; WebM remains the fallback. */
    hevcVideoUrl: z.string().url().nullable().optional(),
    posterUrl: z.string().url().nullable(),
    ctas: z.array(z.object({
        id: z.string().uuid(),
        label: z.string().min(1).max(80),
        url: CtaDestinationSchema,
        trigger: CtaSchema.shape.trigger,
        position: CtaSchema.shape.position,
        style: CtaSchema.shape.style,
    })),
});
export const AgentPublicBrandSchema = z.object({
    name: z.string().min(1).max(120),
    logoUrl: z.string().url().nullable(),
    brandColor: BrandColorSchema.nullable(),
});
export const AgentPublicConfigSchema = z.object({
    agentId: z.string().uuid(),
    name: z.string(),
    brand: AgentPublicBrandSchema,
    interactionMode: AgentSchema.shape.interactionMode,
    textChatFallbackEnabled: AgentSchema.shape.textChatFallbackEnabled,
    lipAnimationEnabled: AgentSchema.shape.lipAnimationEnabled,
    /** Provider identifier only — voiceProviderConfig is intentionally omitted. */
    voiceProvider: z.enum(['openai-realtime', 'vapi']),
    videoUrl: z.string().url().nullable(),
    /** Safari-compatible HEVC-with-alpha companion; WebM remains the fallback. */
    hevcVideoUrl: z.string().url().nullable().optional(),
    posterUrl: z.string().url().nullable(),
    backgroundKind: z.enum(['color', 'image']),
    backgroundValue: z.string(),
    languages: z.array(Iso639_1Schema),
    defaultLang: Iso639_1Schema,
    ctas: z.array(CtaSchema),
    localized: z.record(Iso639_1Schema, AgentPublicLocalizedConfigSchema).default({}),
    status: z.enum(['active', 'paused', 'archived']),
});
// =========================================================
// Session request / response
// =========================================================
export const AgentSessionRequestSchema = z.object({
    lang: Iso639_1Schema,
    origin: z.string().url(),
});
export const AgentSessionResponseSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('openai-realtime'),
        clientSecret: z.string(),
        expiresAt: TimestampSchema,
        instructions: z.string(),
        model: z.string(),
        voice: OpenAIVoiceSchema,
        // Defaults preserve compatibility while the backend and static runtime
        // deployments roll out independently, and when a cached descriptor was
        // minted by the previous backend version.
        runtimePolicy: AgentSchema.shape.behaviorPolicy.default(DEFAULT_AGENT_BEHAVIOR_POLICY),
        knowledgePolicy: z
            .object({
            enabled: z.boolean(),
            lookupPolicy: z.enum(['factual', 'every-turn']),
        })
            .default({ enabled: true, lookupPolicy: 'every-turn' }),
    }),
    z.object({
        kind: z.literal('vapi'),
        assistantId: z.string(),
        publicKey: z.string(),
        clientSecret: z.string(),
        expiresAt: TimestampSchema,
    }),
]);
//# sourceMappingURL=agents.js.map